// Anonymous, cookieless visitor analytics beacon. The per-tab `sid` lives in
// sessionStorage (gone on tab close). The persistent `pid` lives in localStorage
// (anonymous random id, user-clearable, used only for repeat-visit / retention
// aggregates - no IP, no third-party scripts, no fingerprinting, no profile).
// Beacon shapes the backend accepts: pageview / event / heartbeat (no type).

const KEY_SID = 'dz.sid'
const KEY_PID = 'dz.pid'

function sid(): string {
  try {
    let s = sessionStorage.getItem(KEY_SID)
    if (!s) { s = rid(); sessionStorage.setItem(KEY_SID, s) }
    return s
  } catch { return 'anon' }
}
function pid(): string {
  try {
    let p = localStorage.getItem(KEY_PID)
    if (!p) { p = rid(); localStorage.setItem(KEY_PID, p) }
    return p
  } catch { return '' }
}
const rid = () => Math.random().toString(36).slice(2) + Date.now().toString(36)

// Screen-size bucket (XS/S/M/L/XL) - low-resolution device class, no fingerprint.
function screenBucket(): string {
  const w = window.innerWidth || 0
  if (w < 480) return 'XS'
  if (w < 768) return 'S'
  if (w < 1100) return 'M'
  if (w < 1600) return 'L'
  return 'XL'
}
// First language tag the browser advertises, truncated to its base (e.g. "en").
function lang(): string {
  return (navigator.language || '').split('-')[0].slice(0, 4)
}
// UTM params (only when the URL actually contains them).
function utm(): Record<string, string> {
  const q = new URLSearchParams(location.search)
  const out: Record<string, string> = {}
  const s = q.get('utm_source'); if (s) out.utm_s = s.slice(0, 60)
  const m = q.get('utm_medium'); if (m) out.utm_m = m.slice(0, 60)
  const c = q.get('utm_campaign'); if (c) out.utm_c = c.slice(0, 60)
  return out
}

let started = false
let scrollMax = 0
// Time-to-first-interaction in ms (set on first pointer/key/scroll), so the
// admin can see how long it took a visitor to actually do something.
let ttiMs: number | null = null
// Rage-click counter (>=3 clicks within 1s on roughly the same area). A signal
// of frustration; high values mean a button/area doesn't behave as expected.
let rageClicks = 0
// Window errors caught from the page during the session — useful for spotting
// breakage that real users hit but we never see.
let errorCount = 0

// Track current scroll-depth high-water mark; sent with heartbeats so we know
// how far down a visitor actually read.
function bindScroll() {
  const tick = () => {
    const doc = document.documentElement
    const total = (doc.scrollHeight || 1) - (window.innerHeight || 0)
    if (total > 0) {
      const pct = Math.max(0, Math.min(100, Math.round((window.scrollY / total) * 100)))
      if (pct > scrollMax) scrollMax = pct
    }
  }
  window.addEventListener('scroll', tick, { passive: true })
  tick()
}

// Stamp TTI once, on whichever real interaction happens first. We ignore the
// initial synthetic scroll fired by the scroll listener.
function bindFirstInteraction() {
  const loadedAt = performance.now()
  const mark = () => {
    if (ttiMs == null) ttiMs = Math.max(0, Math.round(performance.now() - loadedAt))
  }
  const opts = { passive: true, capture: true, once: false } as AddEventListenerOptions
  window.addEventListener('pointerdown', mark, opts)
  window.addEventListener('keydown', mark, opts)
  // Genuine scroll past 5% counts; ignore any sub-pixel programmatic scrolls.
  window.addEventListener('scroll', () => {
    if ((window.scrollY || 0) > 30) mark()
  }, { passive: true })
}

// Rage-click detector: keep the last 3 click timestamps + positions and bump
// the counter when 3 clicks land within 1s and within ~60px of each other.
function bindRageClicks() {
  const recent: { t: number; x: number; y: number }[] = []
  window.addEventListener('pointerdown', (e) => {
    const now = performance.now()
    recent.push({ t: now, x: e.clientX, y: e.clientY })
    while (recent.length && now - recent[0].t > 1000) recent.shift()
    if (recent.length >= 3) {
      const xs = recent.map((r) => r.x), ys = recent.map((r) => r.y)
      const dx = Math.max(...xs) - Math.min(...xs)
      const dy = Math.max(...ys) - Math.min(...ys)
      if (dx < 60 && dy < 60) {
        rageClicks = Math.min(50, rageClicks + 1)
        recent.length = 0
      }
    }
  }, { passive: true, capture: true })
}

// Capture JS errors as a coarse count only — no message, no stack, no PII.
function bindErrors() {
  const bump = () => { errorCount = Math.min(50, errorCount + 1) }
  window.addEventListener('error', bump)
  window.addEventListener('unhandledrejection', bump)
}

function send(type: 'pageview' | 'event' | '', extra: Record<string, unknown> = {}) {
  const body = JSON.stringify({
    sid: sid(), pid: pid(),
    path: location.pathname,
    ...(type ? { type } : {}),
    ...extra,
  })
  try {
    if (!type && navigator.sendBeacon) {
      navigator.sendBeacon('/api/track', new Blob([body], { type: 'application/json' }))
    } else {
      fetch('/api/track', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true }).catch(() => {})
    }
  } catch { /* analytics must never break the page */ }
}

export function trackPageview() {
  send('pageview', {
    ref: started ? '' : (document.referrer || ''),
    lang: lang(),
    screen: screenBucket(),
    ...utm(),
  })
  scrollMax = 0
  started = true
}

// Public event helper. Keep `type` to a short snake_case verb so the admin's
// Events tab stays legible. `target` is an opaque id (server id, mod workshop
// id, country code, etc.); `value` is optional free-form context.
export function trackEvent(type: string, target?: string | number, value?: string | number) {
  send('event', {
    ev: type.slice(0, 32),
    ...(target != null ? { target: String(target).slice(0, 120) } : {}),
    ...(value != null ? { value: String(value).slice(0, 200) } : {}),
  })
}

// Heartbeat payload — includes every behaviour signal we want the backend to
// roll up onto the session row. TTI is sent once it's known.
function beat(): Record<string, unknown> {
  const b: Record<string, unknown> = { scroll: scrollMax }
  if (ttiMs != null) b.tti = ttiMs
  if (rageClicks > 0) b.rage = rageClicks
  if (errorCount > 0) b.err = errorCount
  return b
}

export function initTracking() {
  bindScroll()
  bindFirstInteraction()
  bindRageClicks()
  bindErrors()
  trackPageview()
  // Heartbeat every 25 s while the tab is visible -> session duration + scroll high-water.
  setInterval(() => { if (document.visibilityState === 'visible') send('', beat()) }, 25000)
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') send('', beat()) })
  window.addEventListener('pagehide', () => send('', beat()))
}
